import { Eip1193Provider } from './eip1193-provider.model';

export enum WalletConnectorId {
  Reown = 'reown',
}

export enum WalletAccountType {
  Eoa = 'eoa',
}

export enum WalletConnectionErrorCode {
  ChainDisconnected = 'chain-disconnected',
  Disconnected = 'disconnected',
  NoAccounts = 'no-accounts',
  ProviderUnavailable = 'provider-unavailable',
  Unauthorized = 'unauthorized',
  Unknown = 'unknown',
  UnsupportedChain = 'unsupported-chain',
  UnsupportedMethod = 'unsupported-method',
  UserRejected = 'user-rejected',
}

export interface WalletConnectionErrorPayload {
  code: WalletConnectionErrorCode;
  message: string;
}

/** Carries normalized wallet failures across connector and store boundaries. */
export class WalletConnectionError extends Error implements WalletConnectionErrorPayload {
  /** Stores the wallet error code while preserving the standard Error message. */
  constructor(
    readonly code: WalletConnectionErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'WalletConnectionError';
  }
}

export interface WalletIdentity {
  accountAddress: string;
  accountType: WalletAccountType;
  chainId: number;
  chainIdHex: `0x${string}`;
  connectorId: WalletConnectorId;
  ownerAddress: string;
}

export interface WalletConnector {
  /** Starts a user-triggered wallet connection flow. */
  connect(): Promise<WalletIdentity>;
  /** Clears connector-local state without assuming wallet permission revocation. */
  disconnect(): Promise<void>;
  /** Resets connector-owned network selection to the default supported network. */
  resetNetworkSelection(): Promise<void>;
  /** Requests the wallet to switch to the given EVM chain. */
  switchToChain(chainId: number): Promise<void>;
  /** Restores an already authorized identity without prompting the user. */
  getConnectedIdentity(): Promise<WalletIdentity | null>;
  /** Reports whether this connector can operate in the current browser. */
  isAvailable(): boolean;
  /** Closes the connector-owned network selection modal without disconnecting the wallet. */
  closeNetworkSelectionModal(): Promise<void>;
  /** Subscribes to provider account changes and returns a cleanup callback. */
  onAccountsChanged(handler: (accounts: readonly string[]) => void): () => void;
  /** Subscribes to provider chain changes and returns a cleanup callback. */
  onChainChanged(handler: (chainIdHex: `0x${string}`) => void): () => void;
  /** Subscribes to provider disconnect events and returns a cleanup callback. */
  onDisconnect(handler: () => void): () => void;
}

export interface WalletProviderConnector extends WalletConnector {
  /** Returns the connected EIP-1193 provider for controlled signer access. */
  getEip1193Provider(): Promise<Eip1193Provider>;
}
